import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaSearch,
  FaFilter,
  FaSort,
  FaEye,
  FaRedo,
  FaTrash,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import api from '../utils/api';
import { debounce } from 'lodash';
import axios from 'axios';
import Pagination from '../restaurants/Pagination';
import ConfirmationDialog from '../../components/restaurants/ConfirmationDialog';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 5000,
});

const TextMessageCouponPage = () => {
  const { restaurantId, messageId } = useParams();
  const [coupons, setCoupons] = useState([]);
  const [textMessage, setTextMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    currentPage: 1,
    couponsPerPage: 10,
    searchTerm: '',
    statusFilter: 'all',
    dateFilter: { start: null, end: null },
    sortField: 'createdAt',
    sortDirection: 'desc',
  });

  const [selectedCoupons, setSelectedCoupons] = useState([]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);

  const fetchCouponsAndMessage = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ownerToken');

      const [couponsResponse, messageResponse] = await Promise.all([
        api.get(`/restaurants/${restaurantId}/message/${messageId}/coupons`, {
          params: {
            page: filters.currentPage,
            limit: filters.couponsPerPage,
            sortBy: filters.sortField,
            sortOrder: filters.sortDirection,
            status: filters.statusFilter,
            search: filters.searchTerm,
            startDate: filters.dateFilter.start,
            endDate: filters.dateFilter.end,
          },
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get(`/restaurants/${restaurantId}/messages/${messageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      //   console.log(messageResponse);
      setCoupons(couponsResponse.data.coupons);
      setTextMessage(messageResponse.data.messages);
      setPagination({
        currentPage: couponsResponse.data.currentPage,
        totalPages: couponsResponse.data.totalPages,
        totalCoupons: couponsResponse.data.totalCoupons,
        redeemedCount: couponsResponse.data.redeemedCount,
      });
      setError(null);
    } catch (err) {
      setError(
        'Failed to fetch coupons and message details. Please try again.'
      );
      toast.error(
        'Failed to fetch coupons and message details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [restaurantId, messageId, filters]);

  useEffect(() => {
    fetchCouponsAndMessage();
  }, [fetchCouponsAndMessage]);
  // This effect will run once when the component mounts

  const handleSearchChange = debounce((value) => {
    setFilters((prev) => ({ ...prev, searchTerm: value, currentPage: 1 }));
  }, 300);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
      currentPage: filterName === 'currentPage' ? value : 1,
    }));
  };

  const resetFilters = () => {
    setFilters({
      currentPage: 1,
      couponsPerPage: 10,
      searchTerm: '',
      statusFilter: 'all',
      dateFilter: { start: null, end: null },
      sortField: 'createdAt',
      sortDirection: 'desc',
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleSelectCoupon = (couponId) => {
    setSelectedCoupons((prev) =>
      prev.includes(couponId)
        ? prev.filter((id) => id !== couponId)
        : [...prev, couponId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedCoupons(memoizedCoupons.map((coupon) => coupon._id));
    } else {
      setSelectedCoupons([]);
    }
  };

  const deleteSelectedCoupons = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      await api.post(
        `/restaurants/${restaurantId}/delete-coupons`,
        {
          couponIds: selectedCoupons,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Selected coupons deleted successfully');
      fetchCouponsAndMessage();
      setSelectedCoupons([]);
    } catch (error) {
      toast.error('Failed to delete selected coupons');
    }
  };

  const deleteSingleCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem('ownerToken');
      await api.delete(
        `/restaurants/${restaurantId}/coupons/single/${couponId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Coupon deleted successfully');
      fetchCouponsAndMessage();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleDeleteClick = (couponId) => {
    setDeleteAction({ type: 'single', id: couponId });
    setShowConfirmDialog(true);
  };

  const handleDeleteSelectedClick = () => {
    if (selectedCoupons.length > 0) {
      setDeleteAction({ type: 'multiple', ids: selectedCoupons });
      setShowConfirmDialog(true);
    } else {
      toast.warn('No coupons selected');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      if (deleteAction.type === 'single') {
        await api.delete(
          `/restaurants/${restaurantId}/coupons/single/${deleteAction.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Coupon deleted successfully');
      } else if (deleteAction.type === 'multiple') {
        await api.post(
          `/restaurants/${restaurantId}/delete-coupons`,
          {
            couponIds: deleteAction.ids,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Selected coupons deleted successfully');
      }
      fetchCouponsAndMessage();
      setSelectedCoupons([]);
    } catch (error) {
      toast.error('Failed to delete coupon(s)');
    } finally {
      setShowConfirmDialog(false);
      setDeleteAction(null);
    }
  };

  const memoizedCoupons = useMemo(() => coupons, [coupons]);

  return (
    <div className='container mx-auto px-4 py-8'>
      {/* <ToastContainer /> */}
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-3xl font-bold'>
            Generated coupons for selected message
          </h1>
          {textMessage && (
            <>
              <h2 className='text-xl text-gray-600 mt-2'>
                Message Title: {textMessage.title}
              </h2>
              <h2 className='text-xl text-gray-600 mt-2'>
                Content: {textMessage.content}
              </h2>
            </>
          )}
        </div>
        <Link
          to={`/restaurant/${restaurantId}/text-messages`}
          className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-800 transition duration-300 ease-in-out shadow-md text-nowrap'
        >
          Back to Messages
        </Link>
      </div>

      <div className='mb-4 relative'>
        <input
          type='text'
          placeholder='Search coupon code OR phone number...'
          className='w-full pl-12 pr-4 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <FaSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
      </div>

      <div className='mb-4 flex flex-wrap items-center gap-4'>
        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
            value={filters.statusFilter}
          >
            <option value='all'>All Statuses</option>
            <option value='redeemed'>Redeemed</option>
            <option value='active'>Not Redeemed</option>
          </select>
        </div>

        {/* <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>From:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                start: e.target.value,
              })
            }
            value={filters.dateFilter.start || ''}
          />
        </div> */}

        {/* <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>To:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                end: e.target.value,
              })
            }
            value={filters.dateFilter.end || ''}
          />
        </div> */}

        <div className='flex items-center'>
          <FaSort className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('sortField', e.target.value)}
            value={filters.sortField}
          >
            <option value='createdAt'>Sort by Creation Date</option>
            <option value='code'>Sort by Code</option>
            <option value='status'>Sort by Status</option>
            <option value='expirationDate'>Sort by Expiration Date</option>
          </select>
          <button
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onClick={() =>
              handleFilterChange(
                'sortDirection',
                filters.sortDirection === 'asc' ? 'desc' : 'asc'
              )
            }
          >
            {filters.sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button
          onClick={resetFilters}
          className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
        >
          Clear All Filters
        </button>
      </div>

      <div className='mb-4'>
        <p>Total Coupons: {pagination.totalCoupons}</p>
        <p>
          Redeemed: {pagination.redeemedCount} out of {pagination.totalCoupons}
        </p>
      </div>

      <div className='mb-4 flex justify-between items-center'>
        <div>
          {/* <input
            type='checkbox'
            checked={selectedCoupons.length === memoizedCoupons.length}
            onChange={handleSelectAll}
            className='mr-2'
          />
          <span>Select All</span> */}

          <label className='inline-flex items-center'>
            {/* <input
                  type='checkbox'
                  className='form-checkbox'
                  checked={allSelected}
                  onChange={handleAllSubscribersChange}
                />
                <span className='ml-2'>All Subscribers</span> */}
            <input
              type='checkbox'
              checked={selectedCoupons.length === memoizedCoupons.length}
              onChange={handleSelectAll}
              className='mr-2'
            />
            <span>Select All</span>
          </label>
        </div>
        <div>
          <span className='mr-4'>
            Selected: {selectedCoupons.length} out of {memoizedCoupons.length}
          </span>
          {/* <button
            onClick={deleteSelectedCoupons}
            disabled={selectedCoupons.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button> */}
          <button
            onClick={handleDeleteSelectedClick}
            disabled={selectedCoupons.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button>
        </div>
      </div>

      {error ? (
        <div className='text-red-500 text-xl text-center'>{error}</div>
      ) : loading ? (
        <div className='flex justify-center items-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='mt-6'>
          {memoizedCoupons.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {/* // <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'> */}
              {memoizedCoupons.map((coupon) => (
                <CouponCard
                  key={coupon._id}
                  coupon={coupon}
                  //   messageTitle={textMessage?.title}
                  messageContent={textMessage?.content}
                  isSelected={selectedCoupons.includes(coupon._id)}
                  onSelect={() => handleSelectCoupon(coupon._id)}
                  // onDelete={() => deleteSingleCoupon(coupon._id)}
                  onDelete={() => handleDeleteClick(coupon._id)}
                />
              ))}
            </div>
          ) : (
            <p className='text-center'>No coupons found for this message.</p>
          )}
        </div>
      )}

      {/* <div className='flex justify-center mt-5'>
        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
          (number) => (
            <button
              key={number}
              onClick={() => handleFilterChange('currentPage', number)}
              className={`mx-1 px-3 py-1 rounded ${
                pagination.currentPage === number
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {number}
            </button>
          )
        )}
      </div>

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'>Coupons per page:</span>
          <select
            value={filters.couponsPerPage}
            onChange={(e) =>
              handleFilterChange('couponsPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.couponsPerPage + 1}{' '}
            -{' '}
            {Math.min(
              pagination.currentPage * filters.couponsPerPage,
              pagination.totalCoupons
            )}{' '}
            of {pagination.totalCoupons}
          </span>
        </div>
      </div> */}
      {!loading && !error && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={filters.couponsPerPage}
          totalItems={pagination.totalCoupons}
        />
      )}

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'># per page:</span>
          <select
            value={filters.couponsPerPage}
            onChange={(e) =>
              handleFilterChange('messagesPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            {/* <option value='1'>1</option> */}
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.couponsPerPage + 1}{' '}
            -{' '}
            {Math.min(
              pagination.currentPage * filters.couponsPerPage,
              pagination.totalCoupons
            )}{' '}
            of {pagination.totalMessages}
            {/* {pagination.totalCoupons} */}
          </span>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title='Confirm Deletion'
        message={
          deleteAction?.type === 'single'
            ? 'Are you sure you want to delete this coupon? This action cannot be undone.'
            : `Are you sure you want to delete ${deleteAction?.ids.length} selected coupons? This action cannot be undone.`
        }
      />
    </div>
  );
};

const CouponCard = React.memo(
  ({ coupon, messageContent, isSelected, onSelect, onDelete }) => (
    // <div
    //   className={`bg-white shadow-lg p-4 rounded relative ${
    //     isSelected ? 'border-2 border-blue-500' : ''
    //   }`}
    // >
    <div
      className={`bg-white shadow-lg p-4 rounded relative ${
        isSelected ? 'border-2 border-blue-500' : ''
      } cursor-pointer hover:shadow-xl transition-shadow duration-300`}
      onClick={(e) => {
        if (
          e.target.type !== 'checkbox' &&
          e.target.tagName !== 'BUTTON' &&
          e.target.tagName !== 'A'
        ) {
          onSelect();
        }
      }}
    >
      <input
        type='checkbox'
        checked={isSelected}
        onChange={onSelect}
        className='absolute top-2 right-2'
      />

      <h2 className='text-lg font-semibold'>Coupon Code: {coupon.code}</h2>
      {/* <p className='text-sm text-gray-500'>Message Content: {messageContent}</p> */}
      <p className='text-sm text-gray-500'>
        Subscriber Phone: {coupon.customer?.phoneNumber || 'N/A'}
      </p>
      <p className='text-sm text-gray-500'>
        Status: {coupon.isRedeemed ? 'Redeemed' : 'Not Redeemed'}
      </p>
      <p className='text-sm text-gray-500'>
        Created: {new Date(coupon.createdAt).toLocaleString()}
      </p>
      <p className='text-sm text-gray-500'>
        Redeemed Date:{' '}
        {coupon.redeemedAt
          ? new Date(coupon.redeemedAt).toLocaleString()
          : 'N/A'}
      </p>
      <p className='text-sm text-gray-500'>
        Expires:{' '}
        {coupon.expirationDate
          ? new Date(coupon.expirationDate).toLocaleString()
          : 'N/A'}
      </p>
      <div className='mt-4 flex flex-wrap gap-2'>
        <a
          href={`${process.env.REACT_APP_BASE_URL}/redeem/${coupon.code}`}
          target='_blank'
          rel='noopener noreferrer'
          className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
          onClick={(e) => e.stopPropagation()}
        >
          <FaEye className='inline-block mr-2' />
          View Coupon
        </a>
        {/* <button
          onClick={onDelete}
          className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaTrash className='inline-block mr-2' />
          Delete
        </button> */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(coupon._id);
          }}
          className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaTrash className='inline-block mr-2' />
          Delete
        </button>
        {/* {!coupon.isRedeemed && (
        <button
          onClick={() => console.log('Implement resend functionality')}
          className='bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaRedo className='inline-block mr-2' />
          Resend
        </button>
      )} */}
      </div>
    </div>
  )
);

export default TextMessageCouponPage;

// // src/components/TextMessageCouponPage.js
// import React, { useState, useEffect } from 'react';
// import { useParams } from 'react-router-dom';
// import axios from 'axios';
// import { FaSearch, FaFilter, FaSort } from 'react-icons/fa';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
//   timeout: 5000,
// });

// const TextMessageCouponPage = () => {
//   const { messageId, restaurantId } = useParams();
//   const [coupons, setCoupons] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [filters, setFilters] = useState({
//     page: 1,
//     limit: 10,
//     search: '',
//     sort: 'createdAt',
//     order: 'desc',
//   });
//   const [totalPages, setTotalPages] = useState(0);
//   const [stats, setStats] = useState({ totalCoupons: 0, redeemedCount: 0 });

//   useEffect(() => {
//     fetchCoupons();
//   }, [messageId, filters]);

//   const fetchCoupons = async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem('ownerToken');

//       const response = await api.get(
//         `restaurants/${restaurantId}/message/${messageId}/coupons`,
//         {
//           params: filters,
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setCoupons(response.data.coupons);
//       setTotalPages(response.data.totalPages);
//       setStats({
//         totalCoupons: response.data.totalCoupons,
//         redeemedCount: response.data.redeemedCount,
//       });
//       setError(null);
//     } catch (err) {
//       setError('Failed to fetch coupons. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSearchChange = (e) => {
//     setFilters({ ...filters, search: e.target.value, page: 1 });
//   };

//   const handleSortChange = (field) => {
//     setFilters({
//       ...filters,
//       sort: field,
//       order: filters.order === 'asc' ? 'desc' : 'asc',
//       page: 1,
//     });
//   };

//   const handlePageChange = (newPage) => {
//     setFilters({ ...filters, page: newPage });
//   };

//   if (loading) return <div>Loading...</div>;
//   if (error) return <div>{error}</div>;

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <h1 className='text-3xl font-bold mb-6'>Coupons for Message</h1>

//       <div className='mb-4 flex items-center'>
//         <div className='relative flex-grow'>
//           <input
//             type='text'
//             placeholder='Search coupons...'
//             className='w-full pl-10 pr-4 py-2 border rounded'
//             value={filters.search}
//             onChange={handleSearchChange}
//           />
//           <FaSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
//         </div>
//         <button
//           className='ml-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
//           onClick={() => handleSortChange(filters.sort)}
//         >
//           <FaSort /> {filters.sort}
//         </button>
//       </div>

//       <div className='mb-4'>
//         <p>Total Coupons: {stats.totalCoupons}</p>
//         <p>
//           Redeemed: {stats.redeemedCount} out of {stats.totalCoupons}
//         </p>
//       </div>

//       <table className='w-full border-collapse'>
//         <thead>
//           <tr>
//             <th className='border p-2'>Code</th>
//             <th className='border p-2'>Status</th>
//             <th className='border p-2'>Created At</th>
//             <th className='border p-2'>Redeemed At</th>
//             <th className='border p-2'>Expiration Date</th>
//             <th className='border p-2'>Redeem Link</th>
//           </tr>
//         </thead>
//         <tbody>
//           {coupons.map((coupon) => (
//             <tr key={coupon._id}>
//               <td className='border p-2'>{coupon.code}</td>
//               <td className='border p-2'>
//                 {coupon.isRedeemed ? 'Redeemed' : 'Active'}
//               </td>
//               <td className='border p-2'>
//                 {new Date(coupon.createdAt).toLocaleString()}
//               </td>
//               <td className='border p-2'>
//                 {coupon.redeemedAt
//                   ? new Date(coupon.redeemedAt).toLocaleString()
//                   : 'N/A'}
//               </td>
//               <td className='border p-2'>
//                 {coupon.expirationDate
//                   ? new Date(coupon.expirationDate).toLocaleString()
//                   : 'N/A'}
//               </td>
//               <td className='border p-2'>
//                 <a
//                   href={`https://localhost:3000/redeem/${coupon.code}`}
//                   target='_blank'
//                   rel='noopener noreferrer'
//                 >
//                   Redeem Link
//                 </a>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       <div className='mt-4 flex justify-center'>
//         {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
//           <button
//             key={page}
//             onClick={() => handlePageChange(page)}
//             className={`mx-1 px-3 py-1 border rounded ${
//               filters.page === page ? 'bg-blue-500 text-white' : 'bg-white'
//             }`}
//           >
//             {page}
//           </button>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default TextMessageCouponPage;
